// src/utils/eintragsWarteschlange.js
//
// Lokale Warteschlange fuer Stationsergebnisse.
//
// Zweck: Ein Ergebnis darf nicht verloren gehen, nur weil im Moment des
// Speicherns keine Verbindung besteht (Funkloch, Server kurz nicht erreichbar,
// Sitzung abgelaufen). Solche Eintraege landen hier, bleiben im localStorage
// des Geraets erhalten – auch ueber Neuladen und Neuanmeldung hinweg – und
// werden automatisch nachgesendet.
//
// Bewusst NICHT nachgesendet wird, was der Server inhaltlich abgelehnt hat
// (z. B. Team geloescht) oder was inzwischen ein anderes Geraet eingetragen
// hat. Solche Eintraege bleiben als "fehler" bzw. "konflikt" stehen, bis der
// Nutzer entscheidet – ein stilles Ueberschreiben waere genau der Fehler, den
// der Konfliktschutz (409) verhindern soll.

import { triggerSessionExpired } from "./authSession";

const SPEICHER_SCHLUESSEL = "kirta_eintrag_warteschlange_v1";
const SENDE_TIMEOUT_MS = 15000;
const AUTO_INTERVALL_MS = 15000;

const abonnenten = new Set();
const gesendetAbonnenten = new Set();

/**
 * Benachrichtigung, sobald ein Eintrag aus der Warteschlange angenommen wurde –
 * mit dem Serverstand. Der offene Eingabedialog markiert das Team damit als
 * erfasst, statt es nach dem Nachsenden wieder als offen anzuzeigen.
 */
export function beiGesendet(fn) {
  gesendetAbonnenten.add(fn);
  return () => gesendetAbonnenten.delete(fn);
}

// ---------------------------------------------------------------------------
// Speicher

function lesen() {
  try {
    const roh = JSON.parse(localStorage.getItem(SPEICHER_SCHLUESSEL) || "[]");
    return Array.isArray(roh) ? roh : [];
  } catch {
    return [];
  }
}

function schreiben(eintraege) {
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(eintraege));
  } catch (err) {
    // Speicher voll oder gesperrt: nicht still schlucken – der Aufrufer
    // erfaehrt es ueber die Konsole, die Anzeige bleibt beim letzten Stand.
    console.error("[Warteschlange] konnte nicht gespeichert werden:", err);
  }
  for (const fn of abonnenten) {
    try {
      fn(eintraege);
    } catch {
      /* Anzeige-Fehler duerfen die Warteschlange nicht stoeren */
    }
  }
}

const schluesselVon = (payload) => `${payload.team_id}__${Number(payload.station_number)}`;

function neueId() {
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
}

// ---------------------------------------------------------------------------
// Oeffentliche Lese-/Schreibfunktionen

/** Alle Eintraege (Kopie). */
export function warteschlange() {
  return lesen();
}

/** Beobachten; liefert eine Abmeldefunktion. */
export function abonnieren(fn) {
  abonnenten.add(fn);
  // Aenderungen aus anderen Tabs desselben Geraets mitbekommen.
  const beiSpeicher = (e) => {
    if (e.key === SPEICHER_SCHLUESSEL) fn(lesen());
  };
  window.addEventListener("storage", beiSpeicher);
  return () => {
    abonnenten.delete(fn);
    window.removeEventListener("storage", beiSpeicher);
  };
}

/**
 * Eintrag einreihen. `info` traegt nur Anzeigedaten (Teamname, Station,
 * Einheit), damit die Liste ohne geladene Teams lesbar ist.
 *
 * Gibt es fuer dasselbe Team an derselben Station schon einen wartenden
 * Eintrag, wird er ersetzt – der Server kennt ihn ja noch nicht. Dabei bleiben
 * `replaceIfExists` und `expected_timestamp` des ERSTEN Eintrags erhalten:
 * sie beschreiben den Serverstand, auf den sich beide beziehen.
 *
 * Rueckgabe: `{ id, vorher }` – `vorher` ist der ersetzte Eintrag (oder null),
 * damit "Rueckgaengig" ihn wiederherstellen kann.
 */
export function einreihen(payload, info = {}) {
  const liste = lesen();
  const schluessel = schluesselVon(payload);
  const index = liste.findIndex((e) => schluesselVon(e.payload) === schluessel);
  const vorher = index >= 0 ? liste[index] : null;

  const eintrag = {
    id: neueId(),
    payload: vorher
      ? {
          ...payload,
          replaceIfExists: vorher.payload.replaceIfExists,
          expected_timestamp: vorher.payload.expected_timestamp,
        }
      : payload,
    info,
    status: "wartet",
    versuche: 0,
    erstelltAm: new Date().toISOString(),
    letzterFehler: null,
  };

  if (index >= 0) liste[index] = eintrag;
  else liste.push(eintrag);
  schreiben(liste);
  return { id: eintrag.id, vorher };
}

/** Eintrag entfernen; mit `ersatz` an dessen Stelle einen anderen setzen. */
export function entfernen(id, ersatz = null) {
  const liste = lesen();
  const index = liste.findIndex((e) => e.id === id);
  if (index < 0) return;
  if (ersatz) liste[index] = ersatz;
  else liste.splice(index, 1);
  schreiben(liste);
}

/**
 * Konflikt aufloesen: den eigenen Wert trotzdem senden. Ersetzt wird dabei
 * genau der Serverstand, den der Nutzer in der Konfliktanzeige gesehen hat –
 * aendert der sich erneut, gibt es wieder einen Konflikt statt eines stillen
 * Ueberschreibens.
 */
export function trotzdemSenden(id) {
  const liste = lesen();
  const e = liste.find((x) => x.id === id);
  if (!e || !e.serverEintrag) return;
  e.payload = {
    ...e.payload,
    replaceIfExists: true,
    expected_timestamp: e.serverEintrag.timestamp,
  };
  e.status = "wartet";
  e.letzterFehler = null;
  schreiben(liste);
}

// ---------------------------------------------------------------------------
// Senden

/** Stimmt der Serverwert inhaltlich mit dem gesendeten ueberein? */
function gleicherWert(serverEintrag, payload) {
  if (!serverEintrag) return false;
  const gleich = (a, b) => a !== undefined && a !== null && Number(a) === Number(b);
  const wertGleich =
    payload.estimate_value !== undefined
      ? gleich(serverEintrag.estimate_value, payload.estimate_value)
      : gleich(serverEintrag.performance_score, payload.performance_score);
  // Getrimmt vergleichen: der Server speichert den Namen ohne Randleerzeichen.
  const name = (v) => String(v || "").trim();
  return wertGleich && name(serverEintrag.entry_by) === name(payload.entry_by);
}

/**
 * Einen Eintrag an POST /entries senden und das Ergebnis einordnen.
 *
 * Rueckgabe `{ art, ... }`:
 *   "ok"        – gespeichert (`entry` = Serverstand)
 *   "konflikt"  – anderer Wert steht schon da (`entry` = Serverstand)
 *   "abgelehnt" – inhaltlich abgelehnt, Wiederholen hilft nicht (`fehler`)
 *   "netz"      – keine Verbindung/Serverproblem, spaeter erneut (`fehler`)
 *   "auth"      – Sitzung abgelaufen; Eintrag bleibt fuer nach der Anmeldung
 */
export async function sendeEintrag(backendUrl, payload) {
  const token = localStorage.getItem("token");
  if (!token) return { art: "auth" };

  const abbruch = new AbortController();
  const timer = setTimeout(() => abbruch.abort(), SENDE_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${backendUrl}/entries`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
      signal: abbruch.signal,
    });
  } catch (err) {
    return {
      art: "netz",
      fehler: err?.name === "AbortError" ? "Zeitüberschreitung" : "Keine Verbindung zum Server",
    };
  } finally {
    clearTimeout(timer);
  }

  const daten = await res.json().catch(() => ({}));

  if (res.ok) return { art: "ok", entry: daten.entry };
  if (res.status === 401) return { art: "auth" };
  if (res.status === 409 && daten.entry) {
    // Wurde ein frueherer Versuch schon angenommen, nur die Antwort ging
    // verloren (Timeout)? Dann steht jetzt genau unser Wert da – das ist ein
    // Erfolg, kein Konflikt.
    if (gleicherWert(daten.entry, payload)) return { art: "ok", entry: daten.entry };
    return { art: "konflikt", entry: daten.entry };
  }
  if (res.status >= 500 || res.status === 408 || res.status === 429) {
    return { art: "netz", fehler: `Server nicht erreichbar (HTTP ${res.status})` };
  }
  return { art: "abgelehnt", fehler: daten.error || `Abgelehnt (HTTP ${res.status})` };
}

let sendetGerade = false;

/**
 * Alle wartenden Eintraege der Reihe nach senden. Bricht beim ersten
 * Verbindungsproblem ab (die uebrigen haetten dasselbe Problem).
 * Rueckgabe: Anzahl erfolgreich gesendeter Eintraege.
 */
export async function alleSenden(backendUrl) {
  if (sendetGerade) return 0;
  sendetGerade = true;
  let gesendet = 0;
  try {
    for (const e of lesen().filter((x) => x.status === "wartet")) {
      const r = await sendeEintrag(backendUrl, e.payload);
      const liste = lesen();
      const aktuell = liste.find((x) => x.id === e.id);
      if (!aktuell) continue; // inzwischen vom Nutzer entfernt

      if (r.art === "ok") {
        entfernen(e.id);
        gesendet++;
        for (const fn of gesendetAbonnenten) {
          try {
            fn(r.entry || e.payload);
          } catch {
            /* Anzeige-Fehler duerfen das Senden nicht stoeren */
          }
        }
        continue;
      }
      if (r.art === "auth") {
        triggerSessionExpired();
        break;
      }
      aktuell.versuche += 1;
      if (r.art === "konflikt") {
        aktuell.status = "konflikt";
        aktuell.serverEintrag = r.entry;
      } else if (r.art === "abgelehnt") {
        aktuell.status = "fehler";
        aktuell.letzterFehler = r.fehler;
      } else {
        aktuell.letzterFehler = r.fehler;
      }
      schreiben(liste);
      if (r.art === "netz") break;
    }
  } finally {
    sendetGerade = false;
  }
  return gesendet;
}

/**
 * Automatisches Nachsenden: sobald der Browser wieder online ist, wenn der
 * Tab sichtbar wird und alle 15 Sekunden. Liefert eine Stoppfunktion.
 * `beiErfolg(anzahl)` wird aufgerufen, wenn etwas nachgesendet wurde.
 */
export function starteAutoSenden(backendUrl, beiErfolg) {
  const versuchen = async () => {
    if (!lesen().some((e) => e.status === "wartet")) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    const n = await alleSenden(backendUrl);
    if (n > 0 && typeof beiErfolg === "function") beiErfolg(n);
  };
  const beiSichtbar = () => {
    if (document.visibilityState === "visible") versuchen();
  };

  window.addEventListener("online", versuchen);
  document.addEventListener("visibilitychange", beiSichtbar);
  const intervall = setInterval(versuchen, AUTO_INTERVALL_MS);
  versuchen();

  return () => {
    window.removeEventListener("online", versuchen);
    document.removeEventListener("visibilitychange", beiSichtbar);
    clearInterval(intervall);
  };
}
